import React, { useEffect, useState } from 'react';
import { ActivityItem, Agent, api, Channel, Dm, FileItem, MemberField, PublicUser, ScheduledItem, SentMessage } from './api';
import { avatarStyle } from './AccountMenu';
import { AgentFace } from './AgentAvatar';
import { CallModal, AvPrefs } from './CallModal';
import { CallLauncher } from './CallLauncher';
import { FileBadge, Icon } from './icons';

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function ActivityView({
  workspaceId,
  onOpenChannel,
}: {
  workspaceId: string | null;
  onOpenChannel?: (id: string) => void;
}) {
  const [items, setItems] = useState<ActivityItem[] | null>(null);
  useEffect(() => {
    if (!workspaceId) return;
    setItems(null);
    api.activity(workspaceId).then(setItems).catch(() => setItems([]));
  }, [workspaceId]);

  return (
    <main className="fx-chat">
      <header className="fx-chat-head">
        <div className="fx-chat-title">Activity</div>
        <div className="fx-chat-topic">Mentions of you and reactions to your messages</div>
      </header>
      <div className="fx-tl">
        {items === null && <div className="fx-empty-big">Loading…</div>}
        {items?.length === 0 && (
          <div className="fx-empty-big">Nothing yet — mentions and reactions show up here.</div>
        )}
        {items?.map((it, i) => (
          <button
            type="button"
            className="fx-activity"
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            onClick={() => it.channel_id && onOpenChannel?.(it.channel_id)}
          >
            <div className="fx-activity-emoji">{it.type === 'mention' ? '@' : it.emoji}</div>
            <div className="fx-activity-body">
              <div className="fx-activity-line">
                <b>{it.actor_name}</b>{' '}
                {it.type === 'mention'
                  ? `mentioned you${it.channel_name ? ` in #${it.channel_name}` : ''}`
                  : `reacted ${it.emoji} to your message`}
                <span className="fx-activity-time"> · {timeAgo(it.created_at)}</span>
              </div>
              {it.body && <div className="fx-activity-quote">{it.body}</div>}
            </div>
          </button>
        ))}
      </div>
    </main>
  );
}

export function DirectoryModal({
  workspaceId,
  agents,
  onDm,
  onOpenProfile,
  onClose,
  canManage,
  pendingCount,
  onInvite,
  onJoinRequests,
  embedded,
}: {
  workspaceId: string;
  agents: Agent[];
  onDm: (type: 'user' | 'agent', id: string) => void;
  onOpenProfile?: (userId: string) => void;
  onClose: () => void;
  canManage?: boolean;
  pendingCount?: number;
  onInvite?: () => void;
  onJoinRequests?: () => void;
  embedded?: boolean;
}) {
  const [members, setMembers] = useState<PublicUser[] | null>(null);
  const [q, setQ] = useState('');
  const [openMember, setOpenMember] = useState<string | null>(null);
  const [fieldsCache, setFieldsCache] = useState<Record<string, MemberField[]>>({});
  useEffect(() => {
    api.members(workspaceId).then(setMembers).catch(() => setMembers([]));
  }, [workspaceId]);
  const toggleMember = (id: string) => {
    setOpenMember((cur) => (cur === id ? null : id));
    if (!fieldsCache[id]) {
      api.memberFields(workspaceId, id)
        .then((fs) => setFieldsCache((c) => ({ ...c, [id]: fs })))
        .catch(() => setFieldsCache((c) => ({ ...c, [id]: [] })));
    }
  };

  return (
    <div className={embedded ? undefined : 'fx-modal-backdrop'} onClick={embedded ? undefined : onClose}>
      <div className={embedded ? 'fx-people-body' : 'fx-modal fx-dir'} onClick={(e) => e.stopPropagation()}>
        {!embedded && <div className="fx-login-title">Directory</div>}
        <input className="fx-input" placeholder="Search people by name…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
        {canManage && !embedded && (onInvite || onJoinRequests) && (
          <div style={{ display: 'flex', gap: 8, margin: '2px 0 10px', flexWrap: 'wrap' }}>
            {onInvite && (
              <button type="button" className="fx-btn" onClick={onInvite}>Invite people</button>
            )}
            {onJoinRequests && (
              <button type="button" className="fx-btn" onClick={onJoinRequests}>
                Join requests{pendingCount ? ` (${pendingCount})` : ''}
              </button>
            )}
          </div>
        )}
        <div className="fx-dir-sect">People</div>
        {members === null && <div className="fx-empty">Loading…</div>}
        {(members || []).filter((m) => (m.name || m.email || '').toLowerCase().includes(q.toLowerCase())).map((m) => (
          <div key={m.id}>
            <div className="fx-dir-row">
              <span className="fx-agent-av" style={avatarStyle(m)}>
                {m.avatar_url ? '' : (m.name || m.email || '?')[0]?.toUpperCase()}
                <span className={`fx-agent-dot${m.presence === 'away' ? ' is-away' : ''}`} />
              </span>
              <span className="fx-dir-name" style={{ cursor: 'pointer' }} onClick={() => (onOpenProfile ? onOpenProfile(m.id) : toggleMember(m.id))} title="View profile">
                {m.name || m.email}
                {m.status_emoji && <span className="fx-dir-status"> {m.status_emoji}</span>}
                <span className="fx-dir-sub">{m.role ? ` · ${m.role}` : ''}</span>
              </span>
              <button type="button" className="fx-btn" onClick={() => onDm('user', m.id)}>Message</button>
            </div>
            {openMember === m.id && fieldsCache[m.id] && (
              <div style={{ padding: '0 0 10px 46px', display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
                {fieldsCache[m.id].length === 0 ? (
                  <span style={{ fontSize: 12, color: 'var(--fx-muted, #9092a0)' }}>No details shared.</span>
                ) : (
                  fieldsCache[m.id].map((f) => (
                    <span key={f.label} style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--fx-muted, #9092a0)' }}>{f.label}: </span>
                      {Array.isArray(f.value)
                        ? f.value.map((t) => (
                            <span key={t} style={{ border: '1px solid var(--fx-hair,#33343a)', borderRadius: 20, padding: '1px 8px', fontSize: 11.5 }}>{t}</span>
                          ))
                        : <span>{f.value}</span>}
                    </span>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
        <div className="fx-dir-sect">Agents & apps</div>
        {agents.length === 0 && <div className="fx-empty">No agents yet</div>}
        {agents.map((a) => (
          <div className="fx-dir-row" key={a.id}>
            <span className="fx-agent-av" style={{ backgroundColor: 'transparent' }}><AgentFace name={a.name} avatarUrl={a.avatar_url} size={24} /></span>
            <span className="fx-dir-name">{a.name}</span>
            <button type="button" className="fx-btn" onClick={() => onDm('agent', a.id)}>Message</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export type DraftItem = {
  channelId: string;
  text: string;
  at: number;
  kind: 'channel' | 'dm';
  label: string;
  sub: string;
  party?: { type: string; avatar_url?: string | null; presence?: string };
  chKind?: string;
};

function outDate(at?: number | string): string {
  if (!at) return '';
  const d = new Date(at).getTime();
  if (Number.isNaN(d)) return '';
  const now = new Date();
  const dd = new Date(d);
  const sameDay = dd.toDateString() === now.toDateString();
  if (sameDay) return dd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const ord = (n: number) => (n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th');
  const mon = dd.toLocaleString([], { month: 'short' });
  return dd.getFullYear() === now.getFullYear() ? `${mon} ${dd.getDate()}${ord(dd.getDate())}` : `${mon} ${dd.getDate()}, ${dd.getFullYear()}`;
}

function OutRow({
  icon,
  avatarStyle: avStyle,
  title,
  sub,
  preview,
  date,
  onOpen,
  action,
}: {
  icon: React.ReactNode;
  avatarStyle?: React.CSSProperties;
  title: string;
  sub?: string;
  preview: string;
  date: string;
  onOpen?: () => void;
  action?: React.ReactNode;
}) {
  return (
    <div className="fx-outrow">
      <button type="button" className="fx-outrow-open" onClick={onOpen} disabled={!onOpen}>
        <span className="fx-outrow-av" style={avStyle}>{icon}</span>
        <span className="fx-outrow-main">
          <span className="fx-outrow-title">{title}</span>
          {sub && <span className="fx-outrow-sub">{sub}</span>}
          <span className="fx-outrow-preview">{preview || <em>(empty)</em>}</span>
        </span>
        <span className="fx-outrow-date">{date}</span>
      </button>
      {action && <div className="fx-outrow-action">{action}</div>}
    </div>
  );
}

const OUT_ART = (
  <svg viewBox="0 0 96 84" width="96" height="84" aria-hidden="true">
    <circle cx="52" cy="42" r="40" fill="rgba(126,90,224,0.4)" />
    <rect x="30" y="30" width="52" height="34" rx="5" fill="#f4d7e4" />
    <path d="M30 34l26 18 26-18" fill="none" stroke="#d98cae" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="20" y="20" width="30" height="20" rx="3" fill="#fff" transform="rotate(-8 35 30)" />
    <path d="M14 62c6-6 14-8 22-6" fill="none" stroke="#e39a6b" strokeWidth="8" strokeLinecap="round" />
  </svg>
);

export function DraftsView({
  drafts,
  initialTab,
  introDismissed,
  onDismissIntro,
  onOpenConversation,
  onDeleteDraft,
}: {
  drafts: DraftItem[];
  initialTab: 'drafts' | 'scheduled' | 'sent';
  introDismissed: boolean;
  onDismissIntro: () => void;
  onOpenConversation: (id: string) => void;
  onDeleteDraft: (id: string) => void;
}) {
  const [tab, setTab] = useState<'drafts' | 'scheduled' | 'sent'>(initialTab);
  const [editing, setEditing] = useState(false);
  const [sent, setSent] = useState<SentMessage[] | null>(null);
  const [scheduled, setScheduled] = useState<ScheduledItem[] | null>(null);
  useEffect(() => setTab(initialTab), [initialTab]);
  useEffect(() => {
    if (tab === 'sent' && sent === null) api.sent().then(setSent).catch(() => setSent([]));
    if (tab === 'scheduled' && scheduled === null) api.myScheduled().then(setScheduled).catch(() => setScheduled([]));
  }, [tab, sent, scheduled]);
  const cancelScheduled = async (id: string) => {
    await api.cancelScheduled(id).catch(() => {});
    setScheduled((cur) => (cur || []).filter((s) => s.id !== id));
  };

  const convLabel = (kind?: string, nm?: string | null) =>
    `${kind === 'dm' ? '' : '# '}${nm || 'conversation'}`;
  const convIcon = (kind?: string) =>
    kind === 'dm' ? '@' : <Icon name={kind === 'private' ? 'lock' : 'hash'} size={15} />;

  return (
    <main className="fx-chat">
      <header className="fx-chat-head fx-drafts-head">
        <div className="fx-chat-title">Drafts &amp; sent</div>
        <div className="fx-head-actions">
          {tab !== 'sent' && (
            <button type="button" className={`fx-head-action${editing ? ' is-on' : ''}`} onClick={() => setEditing((v) => !v)}>
              <Icon name="compose" size={14} /> {editing ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
      </header>

      <div className="fx-drafts-tabs">
        <button type="button" className={`fx-dtab${tab === 'drafts' ? ' is-active' : ''}`} onClick={() => setTab('drafts')}>
          Drafts {drafts.length > 0 && <span className="fx-tab-count">{drafts.length}</span>}
        </button>
        <button type="button" className={`fx-dtab${tab === 'scheduled' ? ' is-active' : ''}`} onClick={() => setTab('scheduled')}>
          Scheduled {!!scheduled?.length && <span className="fx-tab-count">{scheduled.length}</span>}
        </button>
        <button type="button" className={`fx-dtab${tab === 'sent' ? ' is-active' : ''}`} onClick={() => setTab('sent')}>
          Sent
        </button>
      </div>

      <div className="fx-tl fx-drafts-body">
        {!introDismissed && (
          <div className="fx-drafts-intro">
            <div className="fx-drafts-intro-txt">
              <div className="fx-drafts-intro-title">All your outgoing messages</div>
              <div className="fx-drafts-intro-sub">Everything you send, draft, and schedule lives here.</div>
            </div>
            <div className="fx-drafts-art">{OUT_ART}</div>
            <button type="button" className="fx-drafts-intro-x" onClick={onDismissIntro} aria-label="Dismiss">✕</button>
          </div>
        )}

        {tab === 'drafts' && (
          <>
            {drafts.length === 0 && (
              <div className="fx-empty-big">No drafts yet — start typing in any conversation and it’s saved here.</div>
            )}
            {drafts.map((d) => (
              <OutRow
                key={d.channelId}
                icon={convIcon(d.kind === 'dm' ? 'dm' : d.chKind)}
                avatarStyle={d.party?.avatar_url ? { backgroundImage: `url(${d.party.avatar_url})` } : undefined}
                title={`Draft in ${d.label}`}
                sub={d.sub}
                preview={d.text.slice(0, 120)}
                date={outDate(d.at)}
                onOpen={() => onOpenConversation(d.channelId)}
                action={editing ? (
                  <button
                    type="button"
                    className="fx-outrow-del"
                    title="Delete draft"
                    onClick={() => onDeleteDraft(d.channelId)}
                  >
                    🗑
                  </button>
                ) : undefined}
              />
            ))}
          </>
        )}

        {tab === 'scheduled' && (
          <>
            {scheduled === null && <div className="fx-empty-big">Loading…</div>}
            {scheduled?.length === 0 && (
              <div className="fx-empty-big">Nothing scheduled — use the ⌄ next to Send to schedule a message.</div>
            )}
            {scheduled?.map((s) => (
              <OutRow
                key={s.id}
                icon={convIcon(s.channel_kind)}
                title={`Scheduled in ${convLabel(s.channel_kind, s.channel_name)}`}
                sub={s.send_at ? `Sends ${new Date(s.send_at).toLocaleString()}` : undefined}
                preview={s.body.slice(0, 120)}
                date={outDate(s.send_at)}
                action={editing ? (
                  <button type="button" className="fx-btn" onClick={() => cancelScheduled(s.id)}>Cancel</button>
                ) : undefined}
              />
            ))}
          </>
        )}

        {tab === 'sent' && (
          <>
            {sent === null && <div className="fx-empty-big">Loading…</div>}
            {sent?.length === 0 && <div className="fx-empty-big">Nothing sent yet.</div>}
            {sent?.map((m) => (
              <OutRow
                key={m.id}
                icon={convIcon(m.channel_kind)}
                title={`Sent in ${convLabel(m.channel_kind, m.channel_name)}`}
                preview={m.body.slice(0, 120)}
                date={outDate(m.created_at)}
              />
            ))}
          </>
        )}
      </div>
    </main>
  );
}

export function BrowseChannelsModal({
  channels,
  muted,
  onOpen,
  onToggleMute,
  onCreate,
  onClose,
}: {
  channels: Channel[];
  muted: string[];
  onOpen: (id: string) => void;
  onToggleMute: (id: string) => void;
  onCreate: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const shown = channels.filter((c) => !q || (c.name || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Browse channels</div>
        <input
          className="fx-text-input"
          placeholder="Search channels"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        {shown.length === 0 && <div className="fx-empty">No channels match.</div>}
        {shown.map((c) => {
          const isMuted = muted.includes(c.id);
          return (
            <div className="fx-dir-row" key={c.id}>
              <button type="button" className="fx-dir-open" onClick={() => { onOpen(c.id); onClose(); }}>
                <span className="fx-pre"><Icon name={c.kind === 'private' ? 'lock' : 'hash'} size={15} /></span>
                <span className="fx-dir-name">
                  {c.name}
                  {isMuted && <span className="fx-dir-sub"> · muted</span>}
                  {c.topic && <span className="fx-dir-sub"> — {c.topic}</span>}
                </span>
              </button>
              <button type="button" className="fx-btn" onClick={() => onToggleMute(c.id)}>
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
            </div>
          );
        })}
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" onClick={() => { onClose(); onCreate(); }}>Create a channel</button>
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function RecentDmsModal({
  dms,
  onOpen,
  onStart,
  onClose,
}: {
  dms: Dm[];
  onOpen: (id: string) => void;
  onStart: () => void;
  onClose: () => void;
}) {
  const sorted = [...dms].sort((a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''));
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Recent direct messages</div>
        {sorted.length === 0 && <div className="fx-empty">No DMs yet.</div>}
        {sorted.map((d) => (
          <button type="button" className="fx-dir-row fx-draft-row" key={d.id} onClick={() => { onOpen(d.id); onClose(); }}>
            <span className="fx-agent-av" style={d.dm_with.type === 'agent' ? { backgroundColor: 'transparent' } : avatarStyle(d.dm_with)}>
              {d.dm_with.type === 'agent' ? <AgentFace name={d.name || ''} avatarUrl={d.dm_with.avatar_url} size={24} /> : d.dm_with.avatar_url ? '' : (d.name?.[0] || '?').toUpperCase()}
              <span className={`fx-agent-dot${d.dm_with.presence === 'away' ? ' is-away' : ''}`} />
            </span>
            <span className="fx-dir-name">
              {d.name}
              {d.last_message_at && <span className="fx-dir-sub"> · {timeAgo(d.last_message_at)}</span>}
            </span>
          </button>
        ))}
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" onClick={() => { onClose(); onStart(); }}>Start direct message</button>
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function BrowseAppsModal({
  agents,
  onDm,
  onCreate,
  onClose,
}: {
  agents: Agent[];
  onDm: (type: 'user' | 'agent', id: string) => void;
  onCreate: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Browse apps</div>
        <div className="fx-login-note" style={{ textAlign: 'left' }}>
          Agents are FxM’s apps — each one connects over the bot API with its own token and per-channel permissions.
        </div>
        {agents.length === 0 && <div className="fx-empty">No agents installed yet.</div>}
        {agents.map((a) => (
          <div className="fx-dir-row" key={a.id}>
            <span className="fx-agent-av" style={{ backgroundColor: 'transparent' }}><AgentFace name={a.name} avatarUrl={a.avatar_url} size={24} /></span>
            <span className="fx-dir-name">{a.name}</span>
            <button type="button" className="fx-btn" onClick={() => onDm('agent', a.id)}>Message</button>
          </div>
        ))}
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" onClick={() => { onClose(); onCreate(); }}>Add an agent</button>
          <button type="button" className="fx-login-back" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function CallsView({
  channels,
  dms,
  members,
  meId,
  callsEnabled,
  av,
}: {
  channels: Channel[];
  dms: Dm[];
  members: PublicUser[];
  meId?: string;
  callsEnabled: boolean;
  av?: AvPrefs;
}) {
  const [call, setCall] = useState<{ id: string; name: string; video: boolean } | null>(null);
  const ringAndCall = async (id: string, name: string, memberIds: string[], video: boolean) => {
    try { await api.ringCall(id, memberIds, video); } catch { /* best-effort */ }
    setCall({ id, name, video });
  };

  const row = (id: string, kind: 'channel' | 'dm', icon: React.ReactNode, name: string, sub: string, avStyle?: React.CSSProperties) => (
    <div className="fx-callrow" key={id}>
      <span className="fx-callrow-ic" style={avStyle}>{icon}</span>
      <span className="fx-callrow-main">
        <span className="fx-callrow-name">{name}</span>
        <span className="fx-callrow-sub">{sub}</span>
      </span>
      {callsEnabled ? (
        <CallLauncher
          kind={kind}
          members={members}
          meId={meId}
          onStart={(video) => {
            if (kind === 'channel' && members.length > 150
              && !window.confirm(`This channel reaches ${members.length} people. Start the call anyway?`)) return;
            setCall({ id, name, video });
          }}
          onRing={(ids, video) => ringAndCall(id, name, ids, video)}
        />
      ) : (
        <span className="fx-callrow-off">Calls off</span>
      )}
    </div>
  );

  return (
    <main className="fx-chat">
      <header className="fx-chat-head">
        <div className="fx-chat-title">Calls</div>
        <div className="fx-chat-topic">
          {callsEnabled ? 'Start a voice or video call in any conversation' : 'Voice & video calls'}
        </div>
      </header>

      {call && (
        <CallModal
          conversationId={call.id}
          title={call.name}
          av={av}
          initialCamera={call.video}
          meId={meId}
          onClose={() => setCall(null)}
        />
      )}

      <div className="fx-tl fx-calls-body">
        {!callsEnabled && (
          <div className="fx-calls-note">
            🔇 Calls aren’t connected yet. Set <code>LIVEKIT_URL</code>, <code>LIVEKIT_API_KEY</code> and{' '}
            <code>LIVEKIT_API_SECRET</code> on the server and they light up here — no other changes needed.
          </div>
        )}
        <div className="fx-cmd-group">Channels</div>
        {channels.length === 0 && <div className="fx-empty">No channels</div>}
        {channels.map((c) =>
          row(c.id, 'channel', <Icon name={c.kind === 'private' ? 'lock' : 'hash'} size={15} />, c.name || 'channel',
            c.kind === 'private' ? 'Private channel' : 'Channel'),
        )}
        <div className="fx-cmd-group">Direct messages</div>
        {dms.length === 0 && <div className="fx-empty">No direct messages</div>}
        {dms.map((d) =>
          row(
            d.id,
            'dm',
            d.dm_with.type === 'agent' ? '🤖' : (d.name?.[0] || '?').toUpperCase(),
            d.name || '?',
            d.dm_with.type === 'agent' ? 'Agent' : 'Direct message',
            d.dm_with.avatar_url ? { backgroundImage: `url(${d.dm_with.avatar_url})` } : undefined,
          ),
        )}
      </div>
    </main>
  );
}

export function PlaceholderView({ title, note }: { title: string; note: string }) {
  return (
    <main className="fx-chat fx-main--empty">
      <div>
        <div className="fx-chat-title" style={{ textAlign: 'center' }}>{title}</div>
        <div className="fx-empty-big">{note}</div>
      </div>
    </main>
  );
}

export function FilesView({ workspaceId }: { workspaceId: string | null }) {
  const [items, setItems] = useState<FileItem[] | null>(null);
  useEffect(() => {
    if (!workspaceId) return;
    setItems(null);
    api.files(workspaceId).then(setItems).catch(() => setItems([]));
  }, [workspaceId]);

  return (
    <main className="fx-chat">
      <header className="fx-chat-head">
        <div className="fx-chat-title">Files</div>
        <div className="fx-chat-topic">Everything shared across your channels</div>
      </header>
      <div className="fx-tl">
        {items === null && <div className="fx-empty-big">Loading…</div>}
        {items?.length === 0 && <div className="fx-empty-big">No files shared yet.</div>}
        <div className="fx-files-grid">
          {items?.map((f) => {
            const isImg = (f.mime || '').startsWith('image/');
            return (
              <a className="fx-file-card" key={f.id} href={f.url} target="_blank" rel="noreferrer">
                <div className="fx-file-thumb">
                  {isImg ? <img src={f.url} alt={f.name} /> : <span className="fx-file-ic"><FileBadge name={f.name} size={34} /></span>}
                </div>
                <div className="fx-file-meta">
                  <div className="fx-file-name">{f.name}</div>
                  <div className="fx-file-sub">
                    {f.size ? `${Math.max(1, Math.round(f.size / 1024))} KB` : ''} · {timeAgo(f.created_at)}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </main>
  );
}
